// ==============================
// User Management — "Group" tab
// ==============================
// Clusters customer accounts that share a chosen key (phone / email / both /
// business name / parent) so duplicates are easy to spot and (later) merge.
// Backed by GET /user/groups.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallMerge,
  DeleteOutline,
  EditOutlined,
  ExpandMore,
  Search,
  UnfoldLess,
  UnfoldMore,
  VisibilityOutlined,
} from '@mui/icons-material';

import { apiDelete, apiGet } from '../utils/apiClient';
import { PaginatedResponse, User } from '../types/UserTable.type';
import { userTypeColors } from '../utils/colorHandler';
import { notifyActionSuccess, notifyFailure } from '../utils/toast';
import ColorChip from './common/ColorChip';
import { EmptyValue } from './common/EmptyValue';
import FamilyTreeDialog from './FamilyTreeDialog';
import MergeProfileDialog from './FormDialog/MergeProfileDialog';
import UserInfoDialog from './FormDialog/UserInfoDialog';
import MergeHistoryCard from './MergeHistoryCard';
import { rowHoverBg } from '../utils/rowHighlight';

type GroupByKey = 'phone' | 'email' | 'phone_email' | 'business' | 'parent';

interface UserGroup {
  key: string;
  count: number;
  users: User[];
}

const GROUP_BY_OPTIONS: { value: GroupByKey; label: string }[] = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'phone_email', label: 'Phone and Email' },
  { value: 'business', label: 'Business Name' },
  { value: 'parent', label: 'Parent' },
];

// The value the whole group shares — derived from the first member so the
// display keeps its original casing (the backend key is lowercased for matching).
const groupLabel = (group: UserGroup, groupBy: GroupByKey): React.ReactNode => {
  const first = group.users[0];
  if (!first) return <EmptyValue />;
  switch (groupBy) {
    case 'email':
      return first.email || <EmptyValue />;
    case 'phone_email':
      return `${first.phone || '—'} · ${first.email || '—'}`;
    case 'business':
      return first.businessName || <EmptyValue />;
    case 'parent':
      return first.parent || <EmptyValue />;
    case 'phone':
    default:
      return first.phone || <EmptyValue />;
  }
};

const UserGroupView = () => {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalDuplicateUsers, setTotalDuplicateUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState<GroupByKey>('phone');
  const [loading, setLoading] = useState(false);
  // Family tree popup: the accounts of the group whose eye icon was clicked.
  const [familyTree, setFamilyTree] = useState<{
    users: User[];
    sharedLabel: React.ReactNode;
  } | null>(null);
  // Merge selection is scoped to a single group at a time (you can't merge
  // across different phone/email groups).
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mergeCandidates, setMergeCandidates] = useState<User[] | null>(null);
  // Bumped after a merge so the history card below refreshes.
  const [mergeReloadToken, setMergeReloadToken] = useState(0);
  // Which group accordions are expanded (by group id).
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const setGroupExpanded = (groupId: string, expanded: boolean) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (expanded) next.add(groupId);
      else next.delete(groupId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(
      new Set(groups.map((group) => `${groupBy}_${group.key}`)),
    );
  };

  const collapseAll = () => setExpandedGroups(new Set());

  // Per-row actions (view / edit / delete), mirroring the individual table.
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openUserInfo, setOpenUserInfo] = useState(false);
  const [userInfoViewOnly, setUserInfoViewOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openView = (user: User) => {
    setSelectedUser(user);
    setUserInfoViewOnly(true);
    setOpenUserInfo(true);
  };
  const openEdit = (user: User) => {
    localStorage.setItem('selectedUserType', user.userType);
    setSelectedUser(user);
    setUserInfoViewOnly(false);
    setOpenUserInfo(true);
  };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/user/delete/${deleteTarget.id}?force=false`);
      notifyActionSuccess('Customer deleted.');
      setDeleteTarget(null);
      fetchGroups();
    } catch {
      notifyFailure();
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelected = (groupId: string, userId: number) => {
    if (groupId !== selectedGroupId) {
      // Switching groups resets the selection to just this row.
      setSelectedGroupId(groupId);
      setSelectedIds([userId]);
      return;
    }
    setSelectedIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  };

  const clearSelection = () => {
    setSelectedGroupId(null);
    setSelectedIds([]);
  };

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<PaginatedResponse<UserGroup>>(
        `/user/groups?page=${page}&limit=${limit}&search=${encodeURIComponent(
          searchTerm,
        )}&groupBy=${groupBy}`,
      );
      setGroups(res.data);
      setTotalItems(res.metadata.totalItems);
      setTotalDuplicateUsers(
        (res.metadata as { totalDuplicateUsers?: number })
          .totalDuplicateUsers ?? 0,
      );
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, groupBy]);

  // Re-fetch when paging or the grouping dimension changes (search is applied
  // on Enter, see the field below).
  useEffect(() => {
    fetchGroups();
  }, [page, limit, groupBy]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleGroupByChange = (event: SelectChangeEvent) => {
    setGroupBy(event.target.value as GroupByKey);
    setPage(1);
  };

  const activeLabel =
    GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.label ?? '';

  return (
    <>
      <Paper>
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <TextField
            size="medium"
            placeholder="Search by ID, name, phone or email"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setPage(1);
                fetchGroups();
              }
            }}
            sx={{ width: 500, maxWidth: '100%' }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                size: 'small',
              },
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="user-group-by-label">Group by</InputLabel>
              <Select
                labelId="user-group-by-label"
                label="Group by"
                value={groupBy}
                onChange={handleGroupByChange}
              >
                {GROUP_BY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={<UnfoldMore />}
              onClick={expandAll}
              disabled={groups.length === 0}
            >
              Expand all
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UnfoldLess />}
              onClick={collapseAll}
              disabled={expandedGroups.size === 0}
            >
              Collapse all
            </Button>
          </Stack>
        </Box>

        <Box px={2} pb={1}>
          <Typography variant="body2" color="text.secondary">
            {`${totalItems} group(s) sharing the same ${activeLabel.toLowerCase()} · ${totalDuplicateUsers} account(s)`}
          </Typography>
        </Box>

        <Box px={2} pb={2}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : groups.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No duplicate groups found for {activeLabel.toLowerCase()}.
              </Typography>
            </Box>
          ) : (
            groups.map((group) => {
              const groupId = `${groupBy}_${group.key}`;
              const selectedInGroup =
                selectedGroupId === groupId ? selectedIds : [];
              return (
                <Accordion
                  key={groupId}
                  disableGutters
                  expanded={expandedGroups.has(groupId)}
                  onChange={(_event, isExpanded) =>
                    setGroupExpanded(groupId, isExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Chip
                        label={`${group.count} accounts`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {groupLabel(group, groupBy)}
                      </Typography>
                      <Tooltip title="View family tree">
                        <IconButton
                          size="small"
                          // Keep the accordion from toggling when opening the tree.
                          onClick={(event) => {
                            event.stopPropagation();
                            setFamilyTree({
                              users: group.users,
                              sharedLabel: groupLabel(group, groupBy),
                            });
                          }}
                          aria-label="View family tree"
                        >
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {selectedInGroup.length >= 2 && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<CallMerge />}
                          onClick={(event) => {
                            event.stopPropagation();
                            setMergeCandidates(
                              group.users.filter((user) =>
                                selectedInGroup.includes(user.id),
                              ),
                            );
                          }}
                        >
                          Merge {selectedInGroup.length}
                        </Button>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#4da7db' }}>
                            <TableCell padding="checkbox" />
                            {[
                              'ID',
                              'First Name',
                              'Last Name',
                              'Preferred Name',
                              'Business Name',
                              'Email',
                              'Phone',
                              'Parent',
                              'User Type',
                            ].map((heading) => (
                              <TableCell
                                key={heading}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                <Typography fontWeight="bold" color="#fff">
                                  {heading}
                                </Typography>
                              </TableCell>
                            ))}
                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace: 'nowrap',
                                position: 'sticky',
                                right: -16,
                                zIndex: '1 !important',
                                backgroundColor: '#4da7db',
                              }}
                            >
                              <Typography fontWeight="bold" color="#fff">
                                Actions
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.users.map((user) => (
                            <TableRow
                              key={user.id}
                              selected={selectedInGroup.includes(user.id)}
                              sx={{ '&:hover': { backgroundColor: rowHoverBg } }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small"
                                  checked={selectedInGroup.includes(user.id)}
                                  onChange={() =>
                                    toggleSelected(groupId, user.id)
                                  }
                                  inputProps={{
                                    'aria-label': `Select account ${user.id} to merge`,
                                  }}
                                />
                              </TableCell>
                              <TableCell>{user.id}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.firstName}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.lastName}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.preferredName || <EmptyValue />}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.businessName || <EmptyValue />}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.email || <EmptyValue />}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.phone || <EmptyValue />}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {user.parent &&
                                  user.parent.toLowerCase() !== 'none' ? (
                                  user.parent
                                ) : (
                                  <EmptyValue />
                                )}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <ColorChip
                                  label={user.userType}
                                  palette={userTypeColors}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  position: 'sticky',
                                  right: -16,
                                  zIndex: '1 !important',
                                  backgroundColor: 'background.paper',
                                }}
                              >
                                <Tooltip title="View profile">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label="view profile"
                                    onClick={() => openView(user)}
                                  >
                                    <VisibilityOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label="edit user"
                                    onClick={() => openEdit(user)}
                                  >
                                    <EditOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label="delete user"
                                    onClick={() => setDeleteTarget(user)}
                                  >
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>

        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />

        <FamilyTreeDialog
          open={familyTree !== null}
          onClose={() => setFamilyTree(null)}
          users={familyTree?.users ?? []}
          sharedLabel={familyTree?.sharedLabel}
        />

        <MergeProfileDialog
          open={mergeCandidates !== null}
          onClose={() => setMergeCandidates(null)}
          users={mergeCandidates ?? []}
          onMerged={() => {
            clearSelection();
            fetchGroups();
            setMergeReloadToken((token) => token + 1);
          }}
        />

        <UserInfoDialog
          open={openUserInfo}
          handleClose={() => setOpenUserInfo(false)}
          user={selectedUser}
          fetchUsers={fetchGroups}
          viewOnly={userInfoViewOnly}
        />

        <Dialog
          open={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
        >
          <DialogTitle>Delete customer?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`This will archive "${deleteTarget
                  ? `${deleteTarget.lastName ?? ''} ${deleteTarget.firstName ?? ''
                    }`.trim() || `#${deleteTarget.id}`
                  : ''
                }". You can restore it later.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => void handleDelete()}
              disabled={deleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      <MergeHistoryCard
        reloadToken={mergeReloadToken}
        onReverted={fetchGroups}
      />
    </>
  );
};

export default UserGroupView;
